import React, { useCallback, useEffect, useRef, useState } from 'react';
import toast from '../../../utils/toast';
import { Collapse } from '../../../motion';
import { AppButton } from '../../button/app-button';
import { CloseIcon } from '../../icons/close-icon';
import {
  EnablePushResult,
  PushState,
  browserSupportsPush,
  enablePushOnThisDevice,
  probePushState,
} from '../../notifications/push-device';
import {
  TipActions,
  TipCard,
  TipDismiss,
  TipHead,
  TipProblem,
  TipText,
  TipTitle,
} from './portal-kit.styled';

/**
 * "GET TOLD WHEN SOMEONE ARRIVES" — push, one tap from Home.
 *
 * Turning push on used to live only under Account › Notifications, where a
 * resident had to know to look (UX audit ACCT-TEN-02/03). This card offers the
 * same opt-in where they already are, and runs the SAME flow that screen runs
 * (push-device.ts) — only the words about the outcome are the portal's own.
 *
 * ── WHEN IT SHOWS ──
 * Not when push is already on here, not on a browser that cannot do push (an
 * iPhone tab until Lobbero is added to the home screen), and not once put
 * away. A browser that has BLOCKED notifications still gets the card, saying
 * so instead of offering a button that could only fail.
 *
 * ── PUTTING IT AWAY IS REMEMBERED ON THIS DEVICE ──
 * In localStorage: a per-device convenience, never state anybody relies on.
 * Storage that throws (private mode) just means the tip returns next visit.
 */

export const PUSH_TIP_DISMISSED_KEY = 'lobbero-portal-push-tip-dismissed';

const readDismissed = (): boolean => {
  try {
    return window.localStorage.getItem(PUSH_TIP_DISMISSED_KEY) === '1';
  } catch {
    return false;
  }
};

const rememberDismissed = (): void => {
  try {
    window.localStorage.setItem(PUSH_TIP_DISMISSED_KEY, '1');
  } catch {
    /* Private mode: the tip stays away for this visit, which is enough. */
  }
};

/* A refusal or a failure, in a sentence the resident can act on. Never the
   error itself: "NotAllowedError" means nothing to somebody expecting a
   visitor. */
const PROBLEMS: Record<Exclude<EnablePushResult, 'enabled'>, string> = {
  denied:
    'Your browser blocked notifications for Lobbero. You can allow them again in the browser’s site settings.',
  dismissed: 'Notifications were not turned on. You can try again whenever you like.',
  'not-configured': 'Notifications are not available on this Lobbero account yet.',
  failed: 'We could not turn on notifications on this device just now. Please try again.',
};

type TipState = PushState | 'checking';

export interface PushTip {
  /** The card is on offer: not put away, push not on, and possible here. */
  offered: boolean;
  /** Whether it will be offered is known. False only while the probe runs. */
  settled: boolean;
  /** The browser has blocked notifications for this site. */
  blocked: boolean;
  busy: boolean;
  problem: string;
  turnOn: () => void;
  dismiss: () => void;
}

export const usePushTip = (): PushTip => {
  const [dismissed, setDismissed] = useState(readDismissed);
  const [state, setState] = useState<TipState>(() =>
    browserSupportsPush() ? 'checking' : 'unsupported'
  );
  const [busy, setBusy] = useState(false);
  const [problem, setProblem] = useState('');
  const inFlight = useRef(false);
  const alive = useRef(true);

  useEffect(() => {
    alive.current = true;
    /* No probe for a tip already put away, or a browser that cannot push.
       No reclaim either: the tip only needs to know whether to show, and
       re-registering an endpoint is the preferences screen's job. */
    if (!readDismissed() && browserSupportsPush()) {
      probePushState({ reclaim: false }).then(next => {
        if (alive.current) setState(next);
      });
    }

    return () => {
      alive.current = false;
    };
  }, []);

  const turnOn = useCallback(async () => {
    if (inFlight.current) return;
    inFlight.current = true;
    setBusy(true);
    setProblem('');

    const result = await enablePushOnThisDevice();
    inFlight.current = false;
    if (!alive.current) return;
    setBusy(false);

    if (result === 'enabled') {
      setState('enabled');
      toast.success('Notifications are on for this device. We will tell you when someone arrives.');

      return;
    }
    setProblem(PROBLEMS[result]);
  }, []);

  const dismiss = useCallback(() => {
    rememberDismissed();
    setDismissed(true);
  }, []);

  return {
    offered: !dismissed && (state === 'ready' || state === 'denied'),
    settled: dismissed || state !== 'checking',
    blocked: state === 'denied',
    busy,
    problem,
    turnOn,
    dismiss,
  };
};

const TITLE_ID = 'portal-push-tip-title';

export const PushTipCard: React.FC<{ tip: PushTip }> = ({ tip }) => (
  <Collapse open={tip.offered}>
    <TipCard as="section" tone="brand" aria-labelledby={TITLE_ID}>
      <TipHead>
        <TipTitle id={TITLE_ID}>Get told when someone arrives</TipTitle>
        <TipDismiss
          type="button"
          aria-label="Dismiss notification tip"
          onClick={tip.dismiss}
        >
          <CloseIcon />
        </TipDismiss>
      </TipHead>
      <TipText>
        {tip.blocked
          ? 'Your browser is blocking notifications for Lobbero. Allow them in the browser’s site settings, then come back to this page.'
          : 'We will tell you on this device the moment the front desk signs in a visitor or takes in a parcel for you, even when Lobbero is closed.'}
      </TipText>
      {tip.blocked ? null : (
        <TipActions>
          <AppButton
            onClick={tip.turnOn}
            loading={tip.busy}
            loadingLabel="Turning on notifications"
          >
            Turn on notifications
          </AppButton>
        </TipActions>
      )}
      <TipProblem message={tip.problem} role="alert" />
    </TipCard>
  </Collapse>
);
